import { BitkitBreadcrumb } from '@bitrise/bitkit-v2';
import { ReactNode } from 'react';

export type PageHeaderBreadcrumbItem = { key: string; children: ReactNode } & (
  { href: string; isCurrentPage?: never } | { href?: never; isCurrentPage: true }
);

type PageHeaderBreadcrumbProps = {
  /**
   * Appended to the trail as its current item while the header is collapsed, because the collapsed
   * row hides the heading and shows the title in the trail instead — see `BitkitPageHeader`'s
   * `title` docs. Skipped when the trail already ends in a current item, so the row never announces
   * two current pages.
   */
  currentTitle?: string;
  data: PageHeaderBreadcrumbItem[];
  isSticky?: boolean;
};

/**
 * Renders the data-shaped trail `PageLayout` takes as a prop. Pages that build their own
 * `BitkitBreadcrumb` children pass them to `BitkitPageHeader` directly instead.
 */
const PageHeaderBreadcrumb = ({ currentTitle, data, isSticky }: PageHeaderBreadcrumbProps) => {
  const items = data.map(({ key, href, children }) =>
    href ? (
      <BitkitBreadcrumb.Item key={key} href={href}>
        {children}
      </BitkitBreadcrumb.Item>
    ) : (
      <BitkitBreadcrumb.CurrentItem key={key}>{children}</BitkitBreadcrumb.CurrentItem>
    ),
  );

  return (
    <BitkitBreadcrumb>
      {items}
      {isSticky && Boolean(currentTitle) && data.every(({ href }) => href) && (
        <BitkitBreadcrumb.CurrentItem>{currentTitle}</BitkitBreadcrumb.CurrentItem>
      )}
    </BitkitBreadcrumb>
  );
};

export default PageHeaderBreadcrumb;
